<template>
  <div class="map-wrapper">
    <div class="map-perspective-container" :class="{ 'is-3d': is3D }">
      <div class="map-container" ref="mapContainerRef"></div>
      <div class="map-3d-shadow" v-if="is3D"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import { buildProjectClusters, CLUSTER_ZOOM_THRESHOLD } from '../utils/stationCluster'

// Module-level cache — register map once across all instances
let isChinaMapRegistered = false
let chinaGeoJsonCache = null

export default {
  name: 'MapComponent',
  props: {
    stations: {
      type: Array,
      default: () => []
    },
    is3D: {
      type: Boolean,
      default: false
    }
  },
  emits: ['station-click', 'province-click', 'cluster-click'],
  data() {
    return {
      chartInstance: null,
      selectedProvince: '吉林省',
      selectedStation: null,
      animationTimer: null,
      currentZoom: 6, // 响应式的缩放层级
      roamTimer: null, // 用于防抖的定时器
      pendingViewState: null
    }
  },
  created() {
    // Non-reactive properties to break reactivity loops during frequent updates (zooming/panning)
    this._currentCenter = [126.192, 43.723]
    this._currentZoom = 6
  },
  computed: {
    // 根据缩放层级动态决定显示的站点
    displayStations() {
      // 聚合阈值：当缩放小于 100 时，启用项目聚合
      if (this.currentZoom >= CLUSTER_ZOOM_THRESHOLD) {
        return this.stations;
      }

      return buildProjectClusters(this.stations)
    }
  },
  watch: {
    stations: {
      deep: true,
      handler() {
        if (this.chartInstance) {
          this.updateChart()
        }
      }
    },
    // 监听计算属性变化，重绘图表
    displayStations: {
      deep: true,
      handler() {
        if (this.chartInstance) {
          this.renderChart() // 数据源变了，必须重新渲染
        }
      }
    },
    is3D(val) {
      if (val !== undefined) {
        this.handleModeChange()
      }
    }
  },
  async mounted() {
    await this.loadGeoJson()
    this.initChart()
    this.applyPendingViewState()
    window.addEventListener('resize', this.handleResize)

    // Do not emit default province on mount.
    // Parent Dashboard keeps canonical province state and pushes it down.
  },
  beforeUnmount() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
    if (this.roamTimer) {
      clearTimeout(this.roamTimer)
      this.roamTimer = null
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    syncViewState({ provinceName } = {}) {
      const nextProvince = provinceName || '全国'
      if (!this.chartInstance) {
        this.pendingViewState = { provinceName: nextProvince }
        return
      }
      this.applyProvinceSelection(nextProvince)
    },
    applyPendingViewState() {
      if (!this.pendingViewState) return
      const { provinceName } = this.pendingViewState
      this.pendingViewState = null
      this.applyProvinceSelection(provinceName || '全国')
    },
    applyProvinceSelection(provinceName) {
      const target = provinceName || '全国'
      this.selectedProvince = target === '全国' ? null : target
      this.selectedStation = null
      this.renderChart()
      if (target === '全国') {
        this.resetMapView()
      } else {
        this.zoomToProvince(target)
      }
    },
    // ─── GeoJSON Loading ─────────────────────
    async loadGeoJson() {
      if (!isChinaMapRegistered) {
        try {
          // 1. 优先尝试根目录加载 (适配生产环境 www.geofusion.autosquare.org.cn)
          const res = await axios.get('/china.json')
          chinaGeoJsonCache = Object.freeze(res.data)
          echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
          isChinaMapRegistered = true
        } catch (err) {
          // 2. 如果失败，尝试带前缀的路径 (适配本地开发环境 /geofusion/)
          try {
            const res = await axios.get('/geofusion/china.json')
            chinaGeoJsonCache = Object.freeze(res.data)
            echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
            isChinaMapRegistered = true
          } catch (err2) {
             console.error('[MapComponent] 本地 GeoJSON 加载失败，尝试网络回退:', err2)
             // 3. 最后尝试网络资源兜底
            try {
              const res = await axios.get(
                'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json'
              )
              chinaGeoJsonCache = Object.freeze(res.data)
              echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
              isChinaMapRegistered = true
            } catch (err3) {
              console.error('[MapComponent] 所有 GeoJSON 加载均失败:', err3)
            }
          }
        }
      }

      // 确保初始中心点是准确中心且带合适的缩放比例
      if (chinaGeoJsonCache && this.selectedProvince) {
        const feature = chinaGeoJsonCache.features.find(
          (f) => f.properties.name === this.selectedProvince
        )
        if (feature) {
          const bounds = this.getFeatureBounds(feature)
          if (bounds) {
            this._currentCenter = bounds.center
            this._currentZoom = bounds.zoom
          }
        }
      }
    },

    // ─── Chart Init ──────────────────────────
    initChart() {
      const el = this.$refs.mapContainerRef
      if (!el) return
      this.chartInstance = echarts.init(el)
      this.renderChart()
      this.bindEvents()
    },

    // ─── Unified Rendering (2D & 3D share the same option) ───
    renderChart() {
      if (!this.chartInstance) return

      // 1. 强制隐藏 Tooltip，防止在数据切换瞬间因鼠标移动导致 ECharts 内部索引越界报错
      // 这是解决 "Cannot read properties of undefined (reading 'getRawIndex')" 的关键
      // Wrap in try-catch to avoid any potential errors
      try {
        this.chartInstance.dispatchAction({ type: 'hideTip' });
      } catch (e) {
        console.warn('Failed to hide tooltip:', e);
      }

      const option = this.buildOption()
      
      // 2. 去掉 lazyUpdate: true，使用同步更新确保状态一致性
      this.chartInstance.setOption(option, { notMerge: true })
    },

    updateChart() {
      if (this.chartInstance) {
        // Force clear to remove all previous elements (needed for Geo component sometimes)
        this.chartInstance.clear()
        this.renderChart()
      }
    },

    buildOption() {
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          confine: true, // Prevent tooltip from being cut off or causing overflow issues
          appendToBody: true, // Render tooltip in body to avoid z-index/overflow context issues
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          borderColor: '#00f0ff',
          borderWidth: 1,
          textStyle: { color: '#fff', fontSize: 13 },
          formatter: (params) => {
            if (params.componentType === 'series' && params.seriesType === 'effectScatter') {
              if (!params.data) return ''  // Changed from null to empty string to avoid "Cannot set properties of null" error
              const statusMap = {
                normal: '<span style="color:#10b981">● 正常</span>',
                yellow: '<span style="color:#eab308">● 黄色预警</span>',
                orange: '<span style="color:#f97316">● 橙色预警</span>',
                red: '<span style="color:#ef4444">● 红色预警</span>'
              }
              const s = params.data.status || 'normal'
              
              if (params.data.isCluster) {
                 return `
                  <div style="padding: 4px 2px;">
                    <strong style="color: #00f0ff; font-size: 14px;">${params.name}</strong><br/>
                    <div style="margin-top:4px;">${statusMap[s]} (点击展开)</div>
                    <div style="color: #94a3b8; font-size: 11px; margin-top:2px;">
                      位置: ${params.value[0].toFixed(3)}, ${params.value[1].toFixed(3)}
                    </div>
                  </div>
                `
              }

              return `
                <div style="padding: 4px 2px;">
                  <strong style="color: #00f0ff">${params.name || '未知站点'}</strong><br/>
                  状态: ${statusMap[s] || s}<br/>
                  GPS: ${params.value && params.value.length >= 2 ? params.value[0].toFixed(4) : '--'}, 
                       ${params.value && params.value.length >= 2 ? params.value[1].toFixed(4) : '--'}
                </div>
              `
            }
            return '' // Changed from null to empty string
          }
        },
        geo: {
          map: 'china',
          roam: true,
          selectedMode: 'single',
          zoom: this._currentZoom,
          center: this._currentCenter,
          scaleLimit: { min: 1, max: 100000 }, // 极大放宽缩放限制，支持微观查看
          animationDurationUpdate: 400,
          animationEasingUpdate: 'cubicInOut',
          itemStyle: {
            areaColor: '#0f172a',
            borderColor: '#3b82f6',
            borderWidth: 1
          },
          label: {
            show: true,
            color: 'rgba(255, 255, 255, 0.25)',
            fontSize: 10
          },
          emphasis: {
            itemStyle: {
              areaColor: '#1e3a5f',
              borderColor: '#00f0ff',
              borderWidth: 2
            },
            label: {
              show: true,
              color: '#fff',
              fontSize: 12
            }
          },
          select: {
            itemStyle: {
              areaColor: '#1e40af',
              borderColor: '#00f0ff',
              borderWidth: 2,
              shadowBlur: 15,
              shadowColor: 'rgba(0, 240, 255, 0.4)'
            },
            label: {
              show: true,
              color: '#fff',
              fontWeight: 'bold',
              fontSize: 13
            }
          },
          regions: this.selectedProvince ? [{ name: this.selectedProvince, selected: true }] : []
        },
        series: [
          {
            name: '监测站',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            data: this.displayStations,
            symbolSize: (val, params) => {
              // 聚合点大一些，普通点小一些
              return params.data.isCluster ? 25 : 8;
            },
            showEffectOn: 'render',
            rippleEffect: {
              brushType: 'stroke',
              scale: 3,
              period: 5
            },
            progressive: 200,
            progressiveThreshold: 500,
            itemStyle: {
              color: (params) => {
                const status = (params && params.data) ? params.data.status : 'normal'
                return this.getStatusColor(status)
              },
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            },
            zlevel: 1,
            label: {
              formatter: '{b}',
              position: 'right',
              show: true,
              color: '#f8fafc',
              fontSize: 11,
              textBorderColor: '#000',
              textBorderWidth: 2
            },
            labelLayout: {
              hideOverlap: true
            }
          }
        ]
      }
    },

    getStatusColor(status) {
      const map = {
        red: '#ef4444',
        orange: '#f97316',
        yellow: '#eab308',
        normal: '#10b981'
      }
      return map[status] || '#10b981'
    },

    // ─── Events ──────────────────────────────
    bindEvents() {
      if (!this.chartInstance) return
      this.chartInstance.off('click')
      this.chartInstance.off('geoselectchanged')
      this.chartInstance.off('georoam')

      // Sync tracked state when user manually drags/scrolls the map
      this.chartInstance.on('georoam', () => {
        // Try to hide tip on roam to prevent artifacts, but don't force it too aggressively
        // this.chartInstance.dispatchAction({ type: 'hideTip' });
        
        const geoOpt = this.chartInstance.getOption().geo
        if (geoOpt && geoOpt[0]) {
          this._currentCenter = [...geoOpt[0].center]
          this._currentZoom = geoOpt[0].zoom
          
          // 使用防抖更新响应式状态，避免缩放过程中的频繁重绘导致的闪烁或跳变
          if (this.roamTimer) clearTimeout(this.roamTimer);
          
          this.roamTimer = setTimeout(() => {
            this.currentZoom = geoOpt[0].zoom;
            this.roamTimer = null;
          }, 100);
        }
      })

      // Station click
      this.chartInstance.on('click', 'series', (params) => {
        if (params.seriesType === 'effectScatter' && params.value && Array.isArray(params.value) && params.value.length >= 2) {
          
          // 处理聚合点点击：直接放大并通知父组件
          if (params.data && params.data.isCluster) {
            const [lon, lat] = params.value;
            
            // 通知父组件选中项目
            if (params.data.projectName) {
              this.$emit('cluster-click', { projectName: params.data.projectName });
            }

            // 放大到 20000 倍，确保能拉开距离
            this.animateMapView([lon, lat], 20000); 
            return;
          }

          const stationName = params.name
          if (this.selectedStation === stationName) {
            // Unselect station, go back to province view
            this.selectedStation = null
            this.zoomToProvince(this.selectedProvince || '全国')
            this.$emit('province-click', { name: this.selectedProvince || '全国' })
          } else {
            // Select new station
            this.selectedStation = stationName
            this.$emit('station-click', {
              name: stationName,
              gps: [params.value[0], params.value[1]]
            })
          }
        }
      })

      // Province select/deselect
      this.chartInstance.on('geoselectchanged', (params) => {
        // 安全检查：防止 params.allSelected 为空或结构不符合预期
        if (!params || !params.allSelected) {
           // 处理异常或未选中任何区域的情况
           this.selectedProvince = null
           this.resetMapView()
           this.$emit('province-click', { name: '全国' })
           return
        }

        const selected = params.allSelected[0]
        
        // ECharts 的 geoselectchanged 事件有时返回的数据结构中 selected.name 是 undefined (特别是取消选择时)
        // 所以我们必须非常小心地检查每一层
        if (selected && selected.name && Array.isArray(selected.name) && selected.name.length > 0) {
          const provinceName = selected.name[0]

          if (this.selectedProvince === provinceName) {
            // Same province clicked again — deselect and reset
            this.selectedProvince = null
            this.selectedStation = null
            
            // 避免频繁调用 dispatchAction 导致重绘冲突，使用 try-catch
            try {
                this.chartInstance.dispatchAction({
                  type: 'geoUnSelect',
                  name: provinceName
                })
            } catch(e) { console.warn(e) }
            
            this.resetMapView()
            this.$emit('province-click', { name: '全国' })
          } else {
            // New province selected
            this.selectedProvince = provinceName
            
            // 确保 chinaGeoJsonCache 已加载
            if (chinaGeoJsonCache && chinaGeoJsonCache.features) {
                const feature = chinaGeoJsonCache.features.find(f => f.properties.name === provinceName)
                const bounds = feature ? this.getFeatureBounds(feature) : null
                this.zoomToProvince(provinceName)
                this.$emit('province-click', {
                  name: provinceName,
                  center: bounds ? bounds.center : null
                })
            }
          }
        } else {
          // Nothing selected (or deselected via API)
          this.selectedProvince = null
          this.resetMapView()
          this.$emit('province-click', { name: '全国' })
        }
      })
    },

    // ─── Province Zoom ───────────────────────
    zoomToProvince(provinceName) {
      if (!chinaGeoJsonCache) return

      const feature = chinaGeoJsonCache.features.find(
        (f) => f.properties.name === provinceName
      )
      if (!feature) return

      const bounds = this.getFeatureBounds(feature)
      if (!bounds) return

      // 调用 animateMapView，它会应用全局的缓动配置
      this.animateMapView(bounds.center, bounds.zoom)
    },

    getFeatureBounds(feature) {
      const coords = feature.geometry.coordinates
      let minX = Infinity, maxX = -Infinity
      let minY = Infinity, maxY = -Infinity
      let hasPoints = false

      const processCoords = (arr) => {
        if (typeof arr[0] === 'number') {
          hasPoints = true
          if (arr[0] < minX) minX = arr[0]
          if (arr[0] > maxX) maxX = arr[0]
          if (arr[1] < minY) minY = arr[1]
          if (arr[1] > maxY) maxY = arr[1]
        } else {
          arr.forEach(processCoords)
        }
      }

      processCoords(coords)
      if (!hasPoints) return null

      const center = [(minX + maxX) / 2, (minY + maxY) / 2]
      const width = maxX - minX
      const height = maxY - minY

      // 根据省份边界大小计算合适的缩放层级（zoom）
      // 中国地图本身大致跨越 75度经度、50度纬度（基础zoom视作1.0左右）
      // paddingFactor 给予四周一定边距，使得大省份也能显示完整
      const paddingFactor = 0.8
      const scaleW = 75 / (width || 1)
      const scaleH = 50 / (height || 1)
      let zoom = Math.min(scaleW, scaleH) * paddingFactor

      // 限制缩放大小，防止对极小区域过度缩放或者缩放太小
      zoom = Math.max(1.5, Math.min(zoom, 12))

      return { center, zoom }
    },

    resetMapView() {
      if (!this.chartInstance) return
      this.animateMapView([104.114129, 37.550339], 1.2)
    },

    // ─── Native ECharts Animation (Robust) ────
    animateMapView(targetCenter, targetZoom, duration = 1000) {
      if (!this.chartInstance) return

      try {
        this.chartInstance.dispatchAction({ type: 'hideTip' });
      } catch (e) {
        // ignore
      }

      this._currentCenter = [...targetCenter]
      this._currentZoom = targetZoom
      
      // 关键修改：为了确保动画平滑，我们需要显式地设置 option
      // 并且确保 animationDurationUpdate 被应用到本次更新中
      const option = {
        geo: {
          center: targetCenter,
          zoom: targetZoom
        },
        animationDurationUpdate: duration,
        animationEasingUpdate: 'cubicInOut'
      };

      // 关键修改：
      // 1. 不使用 replaceMerge: ['geo']，避免销毁组件导致报错
      // 2. 也不使用 notMerge: false (默认就是 false)，但为了保险显式声明一下
      // 3. 实际上，ECharts 的 merge 机制有时对 geo 的动画支持不够完美
      //    我们尝试手动微调一下，如果当前 zoom 和目标 zoom 差距巨大，
      //    ECharts 可能会选择直接跳转而不是动画。
      //    但我们这里还是信任它的默认行为，只是去掉任何可能干扰合并的配置。
      this.chartInstance.setOption(option, { notMerge: false });
      
      if (this.animationTimer) clearTimeout(this.animationTimer)
      this.animationTimer = setTimeout(() => {
         this.currentZoom = targetZoom
      }, duration + 50)
    },

    // ─── 2D / 3D Toggle (Triggered via prop watch) ────
    handleModeChange() {
      // 在切换前强制重置一下 transition，避免动画冲突
      const el = this.$refs.mapContainerRef
      if(el) {
        el.style.transition = 'none'
        el.style.transform = 'none'
      }

      const doResize = () => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      }
      this.$nextTick(() => {
        setTimeout(doResize, 200)
        setTimeout(doResize, 400)
        setTimeout(doResize, 600)
        setTimeout(doResize, 850)
      })
    },

    zoomToStation(station) {
      if (!station || !station.value) return
      const [lon, lat] = station.value
      // 经计算，两点间距约 0.0002 度 (~20米)
      // 在 Zoom=2000 时仅相距 6px，必须放大到 20000 倍以上才能清晰分离
      // 强制使用 animateMapView 确保走原生动画
      this.animateMapView([lon, lat], 20000)
      this.selectedStation = station.name
    },

    /**
     * 计算一组设备的中心点和自适应缩放层级
     */
    getProjectBounds(devices) {
      if (!devices || devices.length === 0) return null

      let minX = Infinity, maxX = -Infinity
      let minY = Infinity, maxY = -Infinity
      let hasPoints = false

      devices.forEach(d => {
        if (d.value && d.value.length >= 2) {
          const [lng, lat] = d.value
          if (lng < minX) minX = lng
          if (lng > maxX) maxX = lng
          if (lat < minY) minY = lat
          if (lat > maxY) maxY = lat
          hasPoints = true
        }
      })

      if (!hasPoints) return null

      const center = [(minX + maxX) / 2, (minY + maxY) / 2]
      const width = maxX - minX
      const height = maxY - minY

      // 如果范围极小（单点或重合），默认返回较大的缩放
      if (width < 0.0001 && height < 0.0001) {
        return { center, zoom: 20000 }
      }

      // 计算合适的缩放层级
      // 75 和 50 是中国地图经纬度大致跨度
      const paddingFactor = 0.6 // 留出更多边距
      const scaleW = 75 / width
      const scaleH = 50 / height
      let zoom = Math.min(scaleW, scaleH) * paddingFactor

      // 限制缩放范围，防止过大或过小
      zoom = Math.max(3, Math.min(zoom, 40000))

      return { center, zoom }
    },

    /**
     * 缩放到项目聚合视图（Zoom < 100）
     * 显示为聚合点
     */
    zoomToProjectCluster(devices) {
      const bounds = this.getProjectBounds(devices)
      if (!bounds) return
      // 强制缩放到 60 (小于 ZOOM_THRESHOLD=100)，显示聚合点
      this.animateMapView(bounds.center, 60)
    },

    /**
     * 缩放到项目详情视图（Zoom > 100）
     * 显示具体的设备点
     */
    zoomToProjectDetail(devices) {
      const bounds = this.getProjectBounds(devices)
      if (!bounds) return
      
      // 确保缩放层级大于 100，以显示详细站点
      // 如果自动计算的 zoom 小于 120，强制设为 120
      const detailZoom = Math.max(120, bounds.zoom)
      this.animateMapView(bounds.center, detailZoom)
    },

    // 兼容旧接口，默认缩放到详情
    zoomToProject(devices) {
      this.zoomToProjectDetail(devices)
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    }
  }
}
</script>

<style scoped>
.map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.toggle-btn {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(0, 240, 255, 0.4);
  color: #fff;
  padding: 10px 28px;
  border-radius: 30px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 1px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.toggle-btn:hover {
  background: rgba(0, 240, 255, 0.2);
  border-color: #00f0ff;
  color: #fff;
  box-shadow: 0 0 20px rgba(0, 240, 255, 0.4),
    0 0 40px rgba(0, 240, 255, 0.1);
  transform: translateX(-50%) translateY(-2px);
}

.toggle-btn:active {
  transform: translateX(-50%) translateY(0);
}

/* ─── Perspective Container ─── */
.map-perspective-container {
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  position: relative;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1),
    perspective 0.8s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.8s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.8s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.8s cubic-bezier(0.4, 0, 0.2, 1),
    top 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;
  transform-style: preserve-3d;
}

.map-perspective-container.is-3d {
  width: 160%;
  height: 160%;
  left: -30%;
  top: -30%;
  perspective: 900px;
  transform: perspective(900px) rotateX(40deg) scale(0.9);
  transform-origin: center 60%;
}

/* ─── Map Container ─── */
.map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: block;
  will-change: transform;
}

/* ─── 3D Shadow Layer ─── */
.map-3d-shadow {
  position: absolute;
  bottom: -30px;
  left: 5%;
  right: 5%;
  height: 60px;
  background: radial-gradient(ellipse at center,
      rgba(0, 140, 255, 0.15) 0%,
      rgba(0, 140, 255, 0.05) 40%,
      transparent 70%);
  filter: blur(15px);
  pointer-events: none;
  z-index: -1;
}
</style>
